import { is } from "app/pages/sales-intelligence/utils/type-guards";

import type { IFilterPanelBaseFilterModel } from "../types/filters";
import type { SearchFilterInstance } from "../types/searchFilterInstance";

/**
 * A filter whose single panel slot projects onto N flat persisted member keys (e.g. a composed group).
 *
 * Entity-agnostic, generic consumers narrow on {@link isCompositeFilter} and delegate the one-to-many projection.
 *
 * Keeping the contract here, on a distinct composite type, is what lets the universal
 * {@link IFilterPanelBaseFilterModel} stay free of composite/pin vocabulary.
 */
export type CompositeFilterModel<TFragment extends Record<string, unknown> = Record<string, unknown>> =
    IFilterPanelBaseFilterModel<TFragment, unknown, TFragment> & {
        /** The member filter instances this composite presents as one card. */
        getFields(): readonly SearchFilterInstance[];

        /** The flat persisted keys this composite maps to — its members' keys (never the composite's own key). */
        getFieldKeys(): readonly string[];

        /** Pick this composite's member values out of a flat panel map into its nested fragment. */
        collect(flat: Readonly<Record<string, unknown>>): TFragment;

        /** Spread this composite's fragment back to its flat member-keyed entries. */
        expand(fragment?: TFragment): Readonly<Record<string, unknown>>;
    };

export const isCompositeFilter = (model: unknown): model is CompositeFilterModel =>
    is.record(model) && typeof (model as { getFieldKeys?: unknown }).getFieldKeys === "function";
